import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  FileEdit,
  AlertTriangle,
  BarChart3,
  Check,
  FileText,
  RefreshCw,
  Download,
  FolderOpen
} from 'lucide-react';
import Modal from './Modal';
import NotesSection from './NotesSection';
import ProtectedContent from './ProtectedContent';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../utils/authUtils';
import {
  LeadDto,
  CreateLeadDto,
  UpdateLeadDto,
  LeadActivityDto,
  leadsApi,
  validatePhone,
  validateEmail,
  formatDate,
  BenefitType,
  getBenefitTypeOptions,
  getBenefitTypeName
} from '../services/leadsService';
import {
  StatusDto,
  LeadSourceDto,
  statusesApi,
  leadSourcesApi
} from '../services/settingsService';
import { usersApi, UserDto } from '../services/usersService';
import { contractApi, ContractDto, getContractStatusText, getDocumentTypeText } from '../services/contractService';
import InitiateSigningModal from './InitiateSigningModal';
import ContactsEditor, { ContactEntry, convertToContactEntries, convertToUpdateContactDtos, convertToCreateContactDtos } from './ContactsEditor';
import CollapsibleSection from './common/CollapsibleSection';
import SecretaryPicker from './secretary/SecretaryPicker';
import { Secretary, secretariesApi } from '../services/secretaryAssignmentService';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: LeadDto) => void;
  lead?: LeadDto | null;
  mode: 'create' | 'edit' | 'view';
}

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : (props.readOnly ? 'var(--border-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const Select = styled.select<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const Textarea = styled.textarea<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
`;

const SuccessText = styled.span`
  font-size: 12px;
  color: var(--success-color);
  margin-top: 4px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  
  @media (max-width: 767px) {
    flex-direction: column-reverse;
    
    & > * {
      width: 100%;
    }
  }
`;

const DuplicateWarning = styled.div`
  background: var(--warning-light);
  color: var(--warning-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--warning-color);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
`;

const RefreshButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  margin-right: 8px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 115, 234, 0.1);
  }
`;

const ActivitiesSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;

const ActivitiesTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
`;

const ActivityItem = styled.div`
  padding: 12px;
  background: var(--background-color);
  border-radius: var(--border-radius-sm);
  margin-bottom: 8px;
  border-right: 3px solid var(--primary-color);
`;

const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const ActivityDescription = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

const CurrentStatusInfo = styled.div`
  background: var(--primary-light);
  color: var(--primary-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
  font-size: 14px;
  font-weight: 500;
`;

const StatusInfoMessage = styled.div`
  background: var(--background-color);
  color: var(--text-secondary);
  padding: 8px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  margin-top: 4px;
  border-right: 3px solid var(--primary-color);
`;

const LeadModal: React.FC<LeadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lead,
  mode
}) => {
  const { currentUserId, isSystemAdmin } = useAuth();
  
  const [formData, setFormData] = useState<CreateLeadDto | UpdateLeadDto>({
    fullName: '',
    email: '',
    leadSourceId: 0,
    notes: '',
    assignedSalesRepId: undefined,
    benefitType: 0,
    otherBenefitDescription: '',
    contacts: [],
    // עמלת נציג מכירות לעסקה
    salesRepCommissionFixedAmount: 0,
    salesRepCommissionPercentage: 0,
    // שדות משותפים לכל החוזים
    clientAddress: '',
    clientCity: '',
    clientIdNumber: '',
    isClientForeignResident: false,
    // שדות ספציפיים לחוזה ילדים
    fatherName: '',
    fatherIdNumber: '',
    isFatherForeignResident: false,
    motherName: '',
    motherIdNumber: '',
    isMotherForeignResident: false,
    fatherAddress: '',
    fatherCity: '',
    motherAddress: '',
    motherCity: '',
    ...((mode === 'edit' || mode === 'view') && { statusId: 0 })
  });

  // אנשי קשר (טלפון + אימייל אופציונלי)
  const [contactEntries, setContactEntries] = useState<ContactEntry[]>([
    { id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<LeadDto | null>(null);
  const [activities, setActivities] = useState<LeadActivityDto[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [contract, setContract] = useState<ContractDto | null>(null);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [selectedSecretaries, setSelectedSecretaries] = useState<Secretary[]>([]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [allStatusesData, leadSourcesData, salesRepsData] = await Promise.all([
        statusesApi.search('', 1), // Only load lead statuses (group 1)
        leadSourcesApi.getAll(),
        usersApi.getSalesReps() // Load active sales representatives
      ]);

      // Filter statuses based on mode
      let availableStatuses = allStatusesData;

      if (mode === 'view' && lead?.statusId) {
        // In view mode, show only current status
        availableStatuses = allStatusesData.filter(s => s.id === lead.statusId);
      } else if (mode === 'edit') {
        // In edit mode, show all lead statuses (no filtering by next statuses)
        availableStatuses = allStatusesData;
      }

      setStatuses(availableStatuses);
      setLeadSources(leadSourcesData);
      setUsers(salesRepsData);
      
      // Set default lead source if creating new lead
      if (mode === 'create' && leadSourcesData.length > 0) {
        setFormData(prev => ({
          ...prev,
          leadSourceId: leadSourcesData[0].id
        }));
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  }, [lead?.statusId, mode]);

  const loadActivities = async (leadId: number) => {
    try {
      const activitiesData = await leadsApi.getActivities(leadId);
      setActivities(activitiesData);
    } catch (err) {
      console.error('Error loading activities:', err);
    }
  };

  const loadContract = useCallback(async (leadId: number) => {
    try {
      const contractData = await contractApi.getByLeadId(leadId);
      setContract(contractData);
    } catch (err) {
      console.error('Error loading contract:', err);
      setContract(null);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      
      if (lead && (mode === 'edit' || mode === 'view')) {
        setFormData({
          fullName: lead.fullName,
          email: lead.email || '',
          contacts: [],
          leadSourceId: lead.leadSourceId,
          notes: lead.notes || '',
          assignedSalesRepId: lead.assignedSalesRepId,
          statusId: lead.statusId,
          benefitType: lead.benefitType,
          otherBenefitDescription: lead.otherBenefitDescription || '',
          // עמלת נציג מכירות לעסקה
          salesRepCommissionFixedAmount: lead.salesRepCommissionFixedAmount ?? 0,
          salesRepCommissionPercentage: lead.salesRepCommissionPercentage ?? 0,
          // שדות משותפים
          clientAddress: lead.clientAddress || '',
          clientCity: lead.clientCity || '',
          clientIdNumber: lead.clientIdNumber || '',
          isClientForeignResident: lead.isClientForeignResident ?? false,
          // שדות חוזה ילדים
          fatherName: lead.fatherName || '',
          fatherIdNumber: lead.fatherIdNumber || '',
          isFatherForeignResident: lead.isFatherForeignResident ?? false,
          motherName: lead.motherName || '',
          motherIdNumber: lead.motherIdNumber || '',
          isMotherForeignResident: lead.isMotherForeignResident ?? false,
          fatherAddress: lead.fatherAddress || '',
          fatherCity: lead.fatherCity || '',
          motherAddress: lead.motherAddress || '',
          motherCity: lead.motherCity || '',
        });

        // טעינת אנשי קשר
        setContactEntries(convertToContactEntries(lead.contacts));

        loadActivities(lead.id);
        loadContract(lead.id);
      } else {
        setFormData({
          fullName: '',
          email: '',
          contacts: [],
          leadSourceId: 0,
          notes: '',
          assignedSalesRepId: undefined,
          benefitType: 0,
          otherBenefitDescription: '',
          // עמלת נציג מכירות לעסקה
          salesRepCommissionFixedAmount: 0,
          salesRepCommissionPercentage: 0,
          // שדות משותפים
          clientAddress: '',
          clientCity: '',
          clientIdNumber: '',
          isClientForeignResident: false,
          // שדות חוזה ילדים
          fatherName: '',
          fatherIdNumber: '',
          isFatherForeignResident: false,
          motherName: '',
          motherIdNumber: '',
          isMotherForeignResident: false,
          fatherAddress: '',
          fatherCity: '',
          motherAddress: '',
          motherCity: '',
        });
        setContactEntries([
          { id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
        ]);
        setActivities([]);
        setContract(null);
        setSelectedSecretaries([]);
      }
      
      setErrors({});
      setDuplicateWarning(null);
    }
  }, [isOpen, lead, mode, loadInitialData, loadContract]);

  // Periodic contract status refresh for view mode
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isOpen && lead && mode === 'view' && contract) {
      const status = contract.status?.toLowerCase();
      if (status === 'sent' || status === 'viewed' || status === 'partialsigned') {
        // Refresh contract status every 30 seconds for sent contracts
        intervalId = setInterval(() => {
          loadContract(lead.id);
        }, 30000);
      }
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isOpen, lead, mode, contract?.status, contract, loadContract]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName?.trim()) {
      newErrors.fullName = 'שם מלא הוא שדה חובה';
    }

    // וידוא שיש לפחות איש קשר אחד עם טלפון ראשי
    const primaryContact = contactEntries.find(p => p.isPrimary);
    if (!primaryContact || !primaryContact.number?.trim()) {
      newErrors.phone = 'טלפון ראשי הוא שדה חובה';
    } else if (!validatePhone(primaryContact.number)) {
      newErrors.phone = 'מספר טלפון ראשי לא תקין';
    }

    // וידוא שכל הטלפונים תקינים
    const invalidPhones = contactEntries.filter(p => p.number.trim() && !validatePhone(p.number));
    if (invalidPhones.length > 0) {
      newErrors.contacts = 'יש מספרי טלפון לא תקינים';
    }

    // וידוא שכל האימיילים תקינים (אם הוזנו)
    const invalidEmails = contactEntries.filter(p => p.email.trim() && !validateEmail(p.email));
    if (invalidEmails.length > 0) {
      newErrors.contacts = 'יש כתובת דוא"ל לא תקינה';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'כתובת דוא"ל לא תקינה';
    }

    // Only validate leadSourceId in create mode (not edit or view mode)
    if (mode === 'create' && (!formData.leadSourceId || formData.leadSourceId === 0)) {
      newErrors.leadSourceId = 'יש לבחור מקור ליד';
    }

    if (mode === 'edit' && (!('statusId' in formData) || formData.statusId === 0)) {
      newErrors.statusId = 'יש לבחור סטטוס';
    }

    // Validate benefit type (required field)
    if (!formData.benefitType || formData.benefitType === 0) {
      newErrors.benefitType = 'יש לבחור סוג קצבה';
    }

    // Validate otherBenefitDescription when benefitType is Other
    if (formData.benefitType === BenefitType.Other && !formData.otherBenefitDescription?.trim()) {
      newErrors.otherBenefitDescription = 'נא לפרט את סוג הקצבה';
    }

    // Validate sales rep commission (קבוע ≥ 0, אחוז 0–100)
    const fixedCommission = 'salesRepCommissionFixedAmount' in formData ? formData.salesRepCommissionFixedAmount : 0;
    if (fixedCommission != null && fixedCommission < 0) {
      newErrors.salesRepCommissionFixedAmount = 'סכום קבוע חייב להיות חיובי';
    }
    const percentCommission = 'salesRepCommissionPercentage' in formData ? formData.salesRepCommissionPercentage : 0;
    if (percentCommission != null && (percentCommission < 0 || percentCommission > 100)) {
      newErrors.salesRepCommissionPercentage = 'אחוז עמלה חייב להיות בין 0 ל-100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = async (phone: string) => {
    try {
      const duplicate = await leadsApi.checkDuplicate(phone);
      if (duplicate && (mode === 'create' || duplicate.id !== lead?.id)) {
        setDuplicateWarning(duplicate);
      } else {
        setDuplicateWarning(null);
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      let savedLead: LeadDto;

      if (mode === 'edit' && lead) {
        const updateData: UpdateLeadDto = {
          ...(formData as UpdateLeadDto),
          contacts: convertToUpdateContactDtos(contactEntries)
        };
        savedLead = await leadsApi.update(lead.id, updateData);
      } else if (mode === 'create') {
        const createData: CreateLeadDto = {
          ...(formData as CreateLeadDto),
          contacts: convertToCreateContactDtos(contactEntries)
        };
        savedLead = await leadsApi.create(createData);

        if (selectedSecretaries.length > 0 && savedLead?.id) {
          const failures: string[] = [];
          for (const secretary of selectedSecretaries) {
            try {
              await secretariesApi.assignToLead(savedLead.id, secretary.id);
            } catch {
              failures.push(secretary.fullName);
            }
          }
          if (failures.length > 0) {
            toast.error(`הליד נוצר אך נכשלה הקצאת המזכירות: ${failures.join(', ')}`);
          }
        }
      } else {
        // View mode - no save operation
        return;
      }

      onSave(savedLead);
      onClose();
    } catch (err) {
      console.error('Error saving lead:', err);
      // Handle error - could show error message in UI
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNotesChange = (updatedActivities: LeadActivityDto[]) => {
    setActivities(updatedActivities);
  };

  const getModalTitle = (): string => {
    switch (mode) {
      case 'create':
        return 'הוספת ליד חדש';
      case 'edit':
        return 'עריכת ליד';
      case 'view':
        return 'צפייה בליד';
      default:
        return 'ליד';
    }
  };

  const getCurrentStatusName = (): string => {
    if (!lead?.statusId) return '';
    const currentStatus = statuses.find(s => s.id === lead.statusId);
    return currentStatus?.name || '';
  };

  const handleDownloadDocument = async (documentId: number, documentType: string) => {
    try {
      setLoading(true);
      const blob = await contractApi.downloadDocumentPdf(documentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getDocumentTypeText(documentType)}_${contract?.contractNumber || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('שגיאה בהורדת המסמך');
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const shouldShowSaveButton = mode !== 'view';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      width="700px"
      maxWidth="95vw"
      footer={
        <ButtonGroup>
          <Button onClick={onClose} disabled={saving}>
            {mode === 'view' ? 'סגירה' : 'ביטול'}
          </Button>
          {(mode === 'edit' || mode === 'view') && lead && (
            <Button 
              variant="primary" 
              onClick={() => setShowSigningModal(true)}
              disabled={saving || loading}
            >
              <ButtonContent><FileEdit size={16} strokeWidth={2} />חתום לקוח</ButtonContent>
            </Button>
          )}
          {shouldShowSaveButton && (
            <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'שומר...' : 'שמירה'}
            </Button>
          )}
        </ButtonGroup>
      }
    >
      {loading ? (
        <LoadingText>טוען נתונים...</LoadingText>
      ) : (
        <>
          {duplicateWarning && (
            <DuplicateWarning>
              <WarningIcon><AlertTriangle size={18} strokeWidth={2} /></WarningIcon>
              נמצא ליד קיים עם אותו מספר טלפון: {duplicateWarning.fullName} (נוצר ב{formatDate(duplicateWarning.createdAt)})
            </DuplicateWarning>
          )}

          {mode === 'view' && lead && (
            <CurrentStatusInfo>
              <StatusIcon><BarChart3 size={16} strokeWidth={2} /></StatusIcon> סטטוס נוכחי: {getCurrentStatusName()}
            </CurrentStatusInfo>
          )}

          {contract && (
            <CurrentStatusInfo style={{
              backgroundColor: contract.status?.toLowerCase() === 'fullysigned'
                ? '#d1fae5' // ירוק בהיר לחתום במלואו
                : 'var(--primary-light)',
              borderColor: contract.status?.toLowerCase() === 'fullysigned'
                ? '#10b981' // ירוק כהה
                : 'var(--primary-color)'
            }}>
              {contract.status?.toLowerCase() === 'fullysigned' && <StatusIcon><Check size={16} strokeWidth={2} color="#10b981" /></StatusIcon>}
              <StatusIcon><FileText size={16} strokeWidth={2} /></StatusIcon> חוזה: {contract.contractNumber} - סטטוס: {getContractStatusText(contract.status)}
              {mode === 'view' && (
                <RefreshButton
                  onClick={() => lead && loadContract(lead.id)}
                  title="רענן סטטוס חוזה"
                >
                  <RefreshCw size={14} strokeWidth={2} />
                </RefreshButton>
              )}
              {contract.documents && contract.documents.filter(doc => doc.status?.toLowerCase() !== 'signed').length > 0 &&
              contract.expiresAt && new Date(contract.expiresAt) > new Date() && (
                <StatusInfoMessage>
                  תוקף החתימה עד: {new Date(contract.expiresAt).toLocaleDateString('he-IL')}
                </StatusInfoMessage>
              )}

              {/* Download signed documents */}
              {contract.documents && contract.documents.filter(doc => doc.status?.toLowerCase() === 'signed').length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <strong><StatusIcon><Download size={14} strokeWidth={2} /></StatusIcon> הורדת מסמכים חתומים:</strong>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {contract.documents
                      .filter(doc => doc.status?.toLowerCase() === 'signed')
                      .map(doc => (
                        <Button
                          key={doc.id}
                          variant="secondary"
                          onClick={() => handleDownloadDocument(doc.id, doc.documentType)}
                          disabled={loading}
                          style={{ 
                            fontSize: '12px', 
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          <ButtonContent><FolderOpen size={14} strokeWidth={2} />{getDocumentTypeText(doc.documentType)}</ButtonContent>
                        </Button>
                      ))}
                  </div>
                </div>
              )}
            </CurrentStatusInfo>
          )}

          <FormGrid>
            <FormField>
              <RequiredLabel>
                {formData.benefitType === BenefitType.DisabledChildPhysical ||
                 formData.benefitType === BenefitType.DisabledChildMental
                  ? "שם הקטין"
                  : formData.benefitType === BenefitType.NursingCare
                  ? "שם המבוטח"
                  : "שם הלקוח"}
              </RequiredLabel>
              <Input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                hasError={!!errors.fullName}
                placeholder="הזן שם מלא"
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.fullName && <ErrorText>{errors.fullName}</ErrorText>}
            </FormField>

            <FormField fullWidth>
              <RequiredLabel>אנשי קשר</RequiredLabel>
              <ContactsEditor
                contacts={contactEntries}
                onChange={(newEntries) => {
                  setContactEntries(newEntries);
                  // בדיקת כפילות עבור הטלפון הראשי
                  const primary = newEntries.find(p => p.isPrimary);
                  if (primary && primary.number.trim() && validatePhone(primary.number)) {
                    checkDuplicate(primary.number.trim());
                  } else {
                    setDuplicateWarning(null);
                  }
                  if (errors.phone) {
                    setErrors(prev => ({ ...prev, phone: '' }));
                  }
                  if (errors.contacts) {
                    setErrors(prev => ({ ...prev, contacts: '' }));
                  }
                }}
                disabled={isReadOnly}
              />
              {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
              {errors.contacts && <ErrorText>{errors.contacts}</ErrorText>}
            </FormField>

            <FormField>
              <Label>דוא"ל ראשי של הליד</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                hasError={!!errors.email}
                placeholder="example@email.com"
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
              {formData.email && validateEmail(formData.email) && (
                <SuccessText>✓ כתובת דוא"ל תקינה</SuccessText>
              )}
            </FormField>

            <FormField>
              <RequiredLabel>סוג קצבה</RequiredLabel>
              {mode === 'view' ? (
                <Input
                  type="text"
                  value={formData.benefitType ? `${getBenefitTypeName(formData.benefitType)}${formData.benefitType === BenefitType.Other && formData.otherBenefitDescription ? ` - ${formData.otherBenefitDescription}` : ''}` : ''}
                  readOnly
                  disabled
                />
              ) : (
                <Select
                  value={formData.benefitType || 0}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    handleInputChange('benefitType', newValue);
                    // Clear otherBenefitDescription if not "Other"
                    if (newValue !== BenefitType.Other) {
                      handleInputChange('otherBenefitDescription', '');
                    }
                  }}
                  hasError={!!errors.benefitType}
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                >
                  <option value={0}>בחר סוג קצבה</option>
                  {getBenefitTypeOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
              {errors.benefitType && <ErrorText>{errors.benefitType}</ErrorText>}
            </FormField>

            {formData.benefitType === BenefitType.Other && mode !== 'view' && (
              <FormField>
                <RequiredLabel>פירוט סוג קצבה</RequiredLabel>
                <Input
                  type="text"
                  value={formData.otherBenefitDescription || ''}
                  onChange={(e) => handleInputChange('otherBenefitDescription', e.target.value)}
                  hasError={!!errors.otherBenefitDescription}
                  placeholder="אנא פרט את סוג הקצבה"
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                />
                {errors.otherBenefitDescription && <ErrorText>{errors.otherBenefitDescription}</ErrorText>}
              </FormField>
            )}

            {mode !== 'edit' && (
              <FormField>
                <RequiredLabel>מקור ליד</RequiredLabel>
                <Select
                  value={formData.leadSourceId}
                  onChange={(e) => handleInputChange('leadSourceId', parseInt(e.target.value))}
                  hasError={!!errors.leadSourceId}
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                >
                  <option value={0}>בחר מקור ליד</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </Select>
                {errors.leadSourceId && <ErrorText>{errors.leadSourceId}</ErrorText>}
              </FormField>
            )}

            {mode === 'create' && (
              <FormField>
                <Label>נציג מכירות</Label>
                <Select
                  value={formData.assignedSalesRepId || ''}
                  onChange={(e) => {
                    const repId = e.target.value ? parseInt(e.target.value) : undefined;
                    const rep = users.find(u => u.id === repId);
                    // בעת בחירת נציג — מילוי אוטומטי של עמלת ברירת המחדל שלו
                    setFormData(prev => ({
                      ...prev,
                      assignedSalesRepId: repId,
                      ...(rep ? {
                        salesRepCommissionFixedAmount: rep.defaultCommissionFixedAmount ?? 0,
                        salesRepCommissionPercentage: rep.defaultCommissionPercentage ?? 0,
                      } : {}),
                    }));
                  }}
                >
                  <option value="">לא מוקצה</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.fullName}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            {(mode === 'edit' || mode === 'view') && (
              <FormField>
                <Label>נציג מכירות</Label>
                <Input
                  type="text"
                  value={lead?.assignedSalesRepName || 'לא מוקצה'}
                  readOnly
                  disabled
                />
              </FormField>
            )}

            {/* עמלת נציג מכירות לעסקה — קבוע + אחוז (חישוב on-the-fly מול סכום החיוב) */}
            <FormField>
              <Label>עמלת נציג — סכום קבוע (₪)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={('salesRepCommissionFixedAmount' in formData ? formData.salesRepCommissionFixedAmount : 0) ?? 0}
                onChange={(e) => handleInputChange('salesRepCommissionFixedAmount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                hasError={!!errors.salesRepCommissionFixedAmount}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.salesRepCommissionFixedAmount && <ErrorText>{errors.salesRepCommissionFixedAmount}</ErrorText>}
            </FormField>

            <FormField>
              <Label>עמלת נציג — אחוז (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={('salesRepCommissionPercentage' in formData ? formData.salesRepCommissionPercentage : 0) ?? 0}
                onChange={(e) => handleInputChange('salesRepCommissionPercentage', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                hasError={!!errors.salesRepCommissionPercentage}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.salesRepCommissionPercentage && <ErrorText>{errors.salesRepCommissionPercentage}</ErrorText>}
            </FormField>

            {mode === 'edit' && (
              <FormField>
                <RequiredLabel>סטטוס</RequiredLabel>
                <Select
                  value={('statusId' in formData) ? formData.statusId : 0}
                  onChange={(e) => handleInputChange('statusId', parseInt(e.target.value))}
                  hasError={!!errors.statusId}
                >
                  <option value={0}>בחר סטטוס</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name} {status.id === lead?.statusId ? '(נוכחי)' : ''}
                    </option>
                  ))}
                </Select>
                {errors.statusId && <ErrorText>{errors.statusId}</ErrorText>}
                {lead?.statusId && statuses.length > 1 && (
                  <StatusInfoMessage>
                    ✓ מציג את כל הסטטוסים הזמינים של לידים
                  </StatusInfoMessage>
                )}
              </FormField>
            )}

            <FormField fullWidth>
              <Label>הערות</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="הערות נוספות על הליד..."
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </FormField>
          </FormGrid>

          {mode === 'create' && (
            <CollapsibleSection title="מזכירות עם גישה">
              <SecretaryPicker
                selectedSecretaries={selectedSecretaries}
                onChange={setSelectedSecretaries}
              />
            </CollapsibleSection>
          )}

          {mode === 'edit' && (
            <>
              <NotesSection
                leadId={lead?.id || 0}
                activities={activities}
                onNotesChange={handleNotesChange}
                currentUserId={currentUserId || undefined}
                isSystemAdmin={isSystemAdmin}
              />
              
              <ProtectedContent requiredRoles={[UserRole.SYSTEM_ADMIN]}>
                {activities.filter(a => a.activityType !== 6).length > 0 && (
                  <ActivitiesSection>
                    <ActivitiesTitle>היסטוריית פעילות</ActivitiesTitle>
                    {activities
                      .filter(activity => activity.activityType !== 6) // Exclude notes
                      .map(activity => (
                        <ActivityItem key={activity.id}>
                          <ActivityMeta>
                            <span>{activity.activityTypeName}</span>
                            <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                          </ActivityMeta>
                          <ActivityDescription>{activity.description}</ActivityDescription>
                        </ActivityItem>
                      ))}
                  </ActivitiesSection>
                )}
              </ProtectedContent>
            </>
          )}
          
          {mode === 'view' && (
            <>
              {/* Show only existing notes as read-only in view mode */}
              {activities.filter(a => a.activityType === 6).length > 0 && (
                <ActivitiesSection>
                  <ActivitiesTitle>הערות</ActivitiesTitle>
                  {activities
                    .filter(activity => activity.activityType === 6) // Only notes
                    .map(activity => (
                      <ActivityItem key={activity.id}>
                        <ActivityMeta>
                          <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                        </ActivityMeta>
                        <ActivityDescription>{activity.description}</ActivityDescription>
                      </ActivityItem>
                    ))}
                </ActivitiesSection>
              )}
              
              <ProtectedContent requiredRoles={[UserRole.SYSTEM_ADMIN]}>
                {activities.filter(a => a.activityType !== 6).length > 0 && (
                  <ActivitiesSection>
                    <ActivitiesTitle>היסטוריית פעילות</ActivitiesTitle>
                    {activities
                      .filter(activity => activity.activityType !== 6) // Exclude notes
                      .map(activity => (
                        <ActivityItem key={activity.id}>
                          <ActivityMeta>
                            <span>{activity.activityTypeName}</span>
                            <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                          </ActivityMeta>
                          <ActivityDescription>{activity.description}</ActivityDescription>
                        </ActivityItem>
                      ))}
                  </ActivitiesSection>
                )}
              </ProtectedContent>
            </>
          )}
        </>
      )}
      
      {lead && (
        <InitiateSigningModal
          isOpen={showSigningModal}
          onClose={() => setShowSigningModal(false)}
          onSuccess={(newContract) => {
            setContract(newContract);
            setShowSigningModal(false);
          }}
          lead={lead}
        />
      )}
    </Modal>
  );
};

export default LeadModal;